import { useState, useEffect, useMemo } from "react";
import axios from "../../api/axios";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from 'react-router-dom';
import AgentSidebar from "../components/AgentSidebar";
import Navbar from "../../app/components/dashboard/components/navbar/Navbar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import "./app.scss";

const AddProductModal = ({
  isOpen,
  onClose,
  businessName,
  groupId,
  onProductAdded,
  entities,
  selectedProduct: initialSelectedProduct = {},
  selectedEntity: initialSelectedEntity = "",
}) => {
  const [selectedEntity, setSelectedEntity] = useState(initialSelectedEntity);
  const [selectedProduct, setSelectedProduct] = useState(initialSelectedProduct);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const productById = useMemo(() => {
    return new Map(products.map((product) => [Number(product.product_id), product]));
  }, [products]);

  const productOptions = useMemo(() => {
    return products.map((product) => (
      <option key={product.product_id} value={product.product_id}>
        {product.product_name}
      </option>
    ));
  }, [products]);

  const restaurantOptions = useMemo(() => {
    return entities.map((entity) => (
      <option
        key={entity.business_entity_id}
        value={entity.business_entity_id}
      >
        {entity.business_name} | {entity.business_type}
      </option>
    ));
  }, [entities]);

  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      const response = await axios.post(
        `/businessEntities/getAllProductsAndPlans`,
        {},
        { headers: { "Content-Type": "application/json" } }
      );
      setProducts(response.data.products || []);
    } catch {
      setError("Failed to load products");
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && products.length === 0) fetchProducts();
  }, [isOpen, products.length]);

  const handleSave = async () => {
    if (!selectedEntity || !selectedProduct?.product_id) {
      setError("Please select both restaurant and product");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await axios.post(
        `/businessEntities/addProductForBusiness`,
        {
          businessEntityId: selectedEntity,
          productId: selectedProduct.product_id,
          groupId,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      onProductAdded();
      onClose();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        setError(error.response.data.error || "Product is already registered");
      } else if (error.response) {
        setError(`Failed to add product: ${error.response.data.message || error.response.data || 'Server error'}`);
      } else if (error.request) {
        setError("Failed to add product: No response from server. Please check your connection.");
      } else {
        setError("Failed to add product: An unexpected error occurred.");
      }
    } finally {
      setIsLoading(false);
      setSelectedEntity("");
      setSelectedProduct({});
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: "620px", width: "calc(100% - 2rem)" }}
      >
        <div
          className="modal-content"
          style={{ borderRadius: "10px", maxHeight: "calc(100vh - 3rem)", overflowY: "auto" }}
        >
          <div className="d-flex justify-content-between align-items-start border-bottom px-4 py-3">
            <div>
              <h5 className="fw-bold mb-1">
                Add Product
              </h5>
              <div className="text-muted small">
                {businessName.toUpperCase()}
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              style={{
                width: "20px",
                height: "20px"
              }}
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          <div className="px-4 py-4">
            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                Restaurant
              </label>
              <select
                className="form-select"
                value={selectedEntity}
                onChange={(e) => setSelectedEntity(e.target.value)}
              >
                <option value="">Select Restaurant</option>
                {restaurantOptions}
              </select>
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold text-dark">
                Product
              </label>
              <select
                className="form-select"
                value={selectedProduct.product_id || ""}
                disabled={productsLoading}
                onChange={(e) => {
                  const productId = Number(e.target.value);
                  setSelectedProduct(productById.get(productId) || {});
                }}
              >
                <option value="">{productsLoading ? "Loading products..." : "Select Product"}</option>
                {productOptions}
              </select>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-outline-secondary" onClick={onClose} disabled={isLoading}>
                Cancel
              </button>
              <button className="btn btn-primary px-4" onClick={handleSave} disabled={isLoading}>
                {isLoading ? "Adding..." : "Add Product"}
              </button>
            </div>

            {error && (
              <div className="alert alert-danger mt-3 mb-0">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const LicenseKeyModal = ({ isOpen, onClose, licenseKeyData, businessName }) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);

  if (!isOpen) return null;

  const titleBusinessName =
    licenseKeyData?.businessName || businessName || "selected business";
  const hasLicenseKey = Boolean(licenseKeyData?.licenseKey);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">License Key for {titleBusinessName}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {hasLicenseKey ? (
              <div className="mt-3">
                <div className="alert alert-success shadow-sm rounded-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="fs-6">License Key</strong>

                    <span className="badge bg-success">
                      {licenseKeyData.activationStatus}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <div className="flex-grow-1 p-2 bg-light border rounded font-monospace text-break">
                      {licenseKeyData.licenseKey}
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      navigator.clipboard.writeText(licenseKeyData.licenseKey);
                      setOpenSnackbar(true);
                    }}
                  >
                    Copy to Clipboard
                  </button>
                  <button className="btn btn-primary" onClick={onClose}>
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="alert alert-danger">
                License key not found or failed to load.
              </div>
            )}
          </div>
        </div>
      </div>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <MuiAlert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
          License key copied to clipboard!
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

const ReactivateLicenseModal = ({ isOpen, onClose, businessEntityId, businessName, onLicenseReactivated }) => {
  const [reason, setReason] = useState("manual_reactivate");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  if (!isOpen) return null;

  const handleReactivate = async () => {
    if (!reason.trim()) {
      setError("Reason is required");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await axios.post(
        `/api/licenses/reactivate/${businessEntityId}`,
        { reason },
        { headers: { "Content-Type": "application/json" } }
      );

      setOpenSnackbar(true);
      onLicenseReactivated();
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      if (error.response) {
        setError(error.response.data.error || "Failed to reactivate license");
      } else if (error.request) {
        setError("No response from server. Please check your connection.");
      } else {
        setError("An unexpected error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Reactivate License for {businessName}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label htmlFor="reason" className="form-label">
                <strong>Reason for Reactivation:</strong>
              </label>
              <textarea
                id="reason"
                className="form-control"
                rows="3"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for reactivating this license..."
              />
            </div>

            <div className="alert alert-info">
              <i className="bi bi-info-circle-fill me-2"></i>
              <strong>Info:</strong> This will reactivate the license and restore its functionality for all devices.
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleReactivate}
                disabled={isLoading}
              >
                {isLoading ? "Reactivating..." : "Reactivate License"}
              </button>
            </div>

            {error && (
              <div className="alert alert-danger mt-3">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <MuiAlert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
          License reactivated successfully!
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

const RevokeLicenseModal = ({ isOpen, onClose, businessEntityId, businessName, onLicenseRevoked }) => {
  const [reason, setReason] = useState("manual_revoke");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  if (!isOpen) return null;

  const handleRevoke = async () => {
    if (!reason.trim()) {
      setError("Reason is required");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await axios.post(
        `/api/licenses/revoke/${businessEntityId}`,
        { reason },
        { headers: { "Content-Type": "application/json" } }
      );

      setOpenSnackbar(true);
      onLicenseRevoked();
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      if (error.response) {
        setError(error.response.data.error || "Failed to revoke license");
      } else if (error.request) {
        setError("No response from server. Please check your connection.");
      } else {
        setError("An unexpected error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Revoke License for {businessName}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label htmlFor="reason" className="form-label">
                <strong>Reason for Revocation:</strong>
              </label>
              <textarea
                id="reason"
                className="form-control"
                rows="3"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for revoking this license..."
              />
            </div>

            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Warning:</strong> This action cannot be undone. The license will be permanently revoked and will no longer be valid for any device.
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleRevoke}
                disabled={isLoading}
              >
                {isLoading ? "Revoking..." : "Revoke License"}
              </button>
            </div>

            {error && (
              <div className="alert alert-danger mt-3">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <MuiAlert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
          License revoked successfully!
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

const AgentConfigureProduct = () => {
  const [businessEntities, setBusinessEntities] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [licenseKeyModalOpen, setLicenseKeyModalOpen] = useState(false);
  const [licenseKeyData, setLicenseKeyData] = useState(null);
  const [licenseKeyBusinessName, setLicenseKeyBusinessName] = useState("");
  const [revokeLicenseModalOpen, setRevokeLicenseModalOpen] = useState(false);
  const [revokeLicenseData, setRevokeLicenseData] = useState({
    businessEntityId: "",
    businessName: ""
  });
  const [reactivateLicenseModalOpen, setReactivateLicenseModalOpen] = useState(false);
  const [reactivateLicenseData, setReactivateLicenseData] = useState({
    businessEntityId: "",
    businessName: ""
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchData = async () => {
    try {
      const entitiesResponse = await axios.get(`/businessEntities/getAllRegisteredBusinessEntities`, {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      setBusinessEntities(entitiesResponse.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data. Please try again later.");
      setLoading(false);
    }
  };

  const handleShowLicenseKey = async (row) => {
    setLicenseKeyBusinessName(row.businessName || "");
    try {
      const response = await axios.get(`/businessEntities/getLicenseKey/${row.businessEntityId}`, {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      console.log("License key response:", response.data);
      setLicenseKeyData(response.data);
      setLicenseKeyModalOpen(true);
    } catch (error) {
      console.error("Error fetching license key:", error);
      setLicenseKeyData(null);
      setLicenseKeyModalOpen(true);
    }
  }

  const handleRevokeLicense = (row) => {
    setRevokeLicenseData({
      businessEntityId: row.businessEntityId,
      businessName: row.businessName
    });
    setRevokeLicenseModalOpen(true);
  };

  const handleLicenseRevoked = () => {
    fetchData();
  };

  const handleReactivateLicense = (row) => {
    setReactivateLicenseData({
      businessEntityId: row.businessEntityId,
      businessName: row.businessName
    });
    setReactivateLicenseModalOpen(true);
  };

  const handleLicenseReactivated = () => {
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    { field: 'bepm_id', headerName: 'ID', width: 70 },
    { field: 'businessEntityId', headerName: 'Business Entity ID', width: 150 },
    { field: 'businessName', headerName: 'Business Name', width: 200 },
    { field: 'productName', headerName: 'Product Name', width: 150 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <div className={params.value === 1 ? "text-green-600" : "text-red-600"}>
          {params.value === 1 ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 800,
      renderCell: (params) => {
        const row = params.row;
        const is_active = Number(row.is_active);
        const stopGridClick = (event) => {
          event.stopPropagation();
        };

        return (
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-primary btn-sm"
              onMouseDown={stopGridClick}
              onClick={(event) => {
                stopGridClick(event);
                handleConfiguration(row);
              }}
            >
              Configure
            </button>
            <button
              className={`btn btn-sm ${is_active === 1 ? "btn-danger" : "btn-success"}`}
              onMouseDown={stopGridClick}
              onClick={(event) => {
                stopGridClick(event);
                handleToggleStatus(row);
              }}
            >
              {is_active === 1 ? "Deactivate" : "Activate"}
            </button>

            {row.productName.toLowerCase() === "basic pos" && is_active === 1 && (
              <>
                <button
                  className="btn btn-success btn-sm"
                  onMouseDown={stopGridClick}
                  onClick={(event) => {
                    stopGridClick(event);
                    handleShowLicenseKey(row);
                  }}
                >
                  View License Key
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onMouseDown={stopGridClick}
                  onClick={(event) => {
                    stopGridClick(event);
                    handleRevokeLicense(row);
                  }}
                >
                  Revoke License
                </button>
                <button
                  className="btn btn-info btn-sm"
                  onMouseDown={stopGridClick}
                  onClick={(event) => {
                    stopGridClick(event);
                    handleReactivateLicense(row);
                  }}
                >
                  Reactivate License
                </button>
              </>
            )}

          </div>
        );
      },
    }
  ];


  const handleAddProduct = (entity) => {
    setSelectedBusiness(entity);
    setModalOpen(true);
    console.log(entity);
  };

  const handleProductAdded = () => {
    fetchData();
  };

  if (loading) {
    return (
      <div className="new">
        <AgentSidebar />
        <div className="newContainer flex flex-col  h-screen overflow-hidden">
          <Navbar />
          <div className="container flex-grow overflow-y-auto px-4 py-8">
            <h1 className="fw-bold mb-4" style={{ fontSize: "2rem" }}>
              Products Configuration
            </h1>
            <div className="text-center py-5">
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5 text-danger">
        {error}
      </div>
    );
  }


  const handleConfiguration = (row) => {
    const productType = row.productName.toLowerCase();


    if (productType === 'special billing' || productType === 'lite billing'  || productType ===  "basic pos") {
      const configurePath = `${row.businessName}/${row.productName}`;
      sessionStorage.setItem('configurePath', configurePath);
      sessionStorage.setItem('configBizEntityId', row.businessEntityId);
      navigate(`/agents/config/products/lite-special`);
    } else if (productType === 'advanced inventory') {
      const advancedConfigPath = `${row.businessName}/advanced-inventory`;
      sessionStorage.setItem('configurePath', advancedConfigPath);
      sessionStorage.setItem('configBizEntityId', row.businessEntityId);
      navigate(`/agents/config/products/advanced-inventory`);
    }
  };

  const handleToggleStatus = async (row) => {
    try {
      const response = await axios.post(
        `/businessEntities/update-status`,
        {
          businessEntityId: row.businessEntityId,
          productId: row.productId,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        }
      );
      showSnackbar(response.data?.message || "Product status updated successfully", "success");
      await fetchData();
    } catch (err) {
      console.error("Error toggling status:", err);
      if (err.response) {
        console.error(`Server error (${err.response.status}): ${err.response.data.message || err.response.data}`);
        showSnackbar(
          err.response.data?.error ||
          err.response.data?.message ||
          "Failed to update product status",
          "error"
        );
      } else if (err.request) {
        console.error("No response received from the server. Please check your connection.");
        showSnackbar("No response from server. Please check your connection.", "error");
      } else {
        console.error("Error setting up the request:", err.message);
        showSnackbar("Failed to update product status", "error");
      }
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredEntities = businessEntities.filter(entity =>
    entity.business_entity_id.toString().includes(searchQuery) ||
    entity.business_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="new">
      <AgentSidebar />
      <div className="newContainer flex flex-col  h-screen overflow-hidden">
        <Navbar />
        <div className="container flex-grow overflow-y-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-6">Product</h1>
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by ID or Business Name..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="border rounded-lg px-4 py-2 w-full"
            />
          </div>

          {filteredEntities.map((entity) => {
            return (
              <div key={entity.group_id} className="mb-8 border rounded-lg overflow-hidden">
                <div className="bg-gray-100 p-4 flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-semibold">{entity.business_name}</h2>
                    <p><strong>ID:</strong> {entity.group_id}</p>
                    <p><strong>Owner:</strong> {entity.name}</p>
                    <p><strong>Contact:</strong> {entity.contact_no}</p>
                    <p><strong>Email:</strong> {entity.email}</p>
                  </div>
                  <button
                    onClick={() => handleAddProduct(entity)}
                    className="bg-green-500 text-white px-4 py-2 rounded"
                  >
                    Add Product
                  </button>
                </div>
                <div style={{ height: 400, width: '100%' }}>
                  <DataGrid
                    rows={entity.businessEntityProductMappings || []}
                    columns={columns}
                    pageSize={5}
                    rowsPerPageOptions={[5]}
                    getRowId={(row) => row.bepm_id}
                    disableSelectionOnClick
                    hideFooterSelectedRowCount
                  />
                </div>
              </div>
            );
          })}
          {businessEntities.length === 0 && (
            <p className="text-center py-4">No matching business entities found.</p>
          )}
          {selectedBusiness && (
            <AddProductModal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              businessName={selectedBusiness.business_name}
              entities={selectedBusiness.entities}
              groupId={selectedBusiness.group_id}
              onProductAdded={handleProductAdded}
            />
          )}
          {licenseKeyModalOpen && (
            <LicenseKeyModal
              isOpen={licenseKeyModalOpen}
              onClose={() => setLicenseKeyModalOpen(false)}
              licenseKeyData={licenseKeyData}
              businessName={licenseKeyBusinessName}
            />
          )}
          {revokeLicenseModalOpen && (
            <RevokeLicenseModal
              isOpen={revokeLicenseModalOpen}
              onClose={() => setRevokeLicenseModalOpen(false)}
              businessEntityId={revokeLicenseData.businessEntityId}
              businessName={revokeLicenseData.businessName}
              onLicenseRevoked={handleLicenseRevoked}
            />
          )}
          {reactivateLicenseModalOpen && (
            <ReactivateLicenseModal
              isOpen={reactivateLicenseModalOpen}
              onClose={() => setReactivateLicenseModalOpen(false)}
              businessEntityId={reactivateLicenseData.businessEntityId}
              businessName={reactivateLicenseData.businessName}
              onLicenseReactivated={handleLicenseReactivated}
            />
          )}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
          >
            <MuiAlert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </MuiAlert>
          </Snackbar>
        </div>
      </div>
    </div>
  );
};

export default AgentConfigureProduct;
